import { TimeZoneType } from "../../constants/timezones"
import { makeDateFnsFormatToken } from "../../utils/date-format"
import { CalendarSettingsType } from "./calendar-settings-id"
import {
  CalendarSettingsDateFormatSpecs,
  CalendarSettingsHoliday,
  CalendarSettingsResponse,
  CalendarSettingsResponseSchema,
} from "./calendar-settings-response"

export type CalendarSettingsViewModel = {
  id: string
  type: CalendarSettingsType
  userId?: string
  orgId?: string
  projectId?: string
  fixedHolidays: string[]
  recurringHolidays: string[]
  holidayDetails: CalendarSettingsHoliday[]
  weekendDays: DayOfWeek[]
  firstDayOfWeek: DayOfWeek
  dateFormatSpecs: CalendarSettingsDateFormatSpecs
  dateFnsDateFormat: string
  timeZone: TimeZoneType
}

const categorizeHolidays = (holidays: CalendarSettingsHoliday[]) => {
  const fixedHolidays: string[] = []
  const recurringHolidays: string[] = []

  holidays.forEach((holiday) => {
    if (holiday.recurring) {
      recurringHolidays.push(holiday.dateKey)
    } else {
      fixedHolidays.push(holiday.dateKey)
    }
  })

  return { fixedHolidays, recurringHolidays }
}

export const makeCalendarSettingsViewModel = (
  calendarSettings: CalendarSettingsResponse["calendarSettingsById"][string]
): CalendarSettingsViewModel => {
  const { dateFormatSpecs, holidays, ...calendarSettingsViewModel } = calendarSettings
  const { fixedHolidays, recurringHolidays } = categorizeHolidays(holidays)

  return {
    ...calendarSettingsViewModel,
    dateFormatSpecs,
    dateFnsDateFormat: makeDateFnsFormatToken(
      dateFormatSpecs.dateOrder,
      dateFormatSpecs.dateSeparator,
      dateFormatSpecs.includeTime
    ),
    firstDayOfWeek: calendarSettings.firstDayOfWeek as DayOfWeek,
    timeZone: calendarSettings.timeZone as TimeZoneType,
    weekendDays: calendarSettings.weekendDays as DayOfWeek[],
    fixedHolidays,
    recurringHolidays,
    holidayDetails: calendarSettings.holidays,
  }
}

export const makeCalendarSettingsViewModelById = (
  response: CalendarSettingsResponse
): CalendarSettingsViewModelById => {
  try {
    const parsedResponse = CalendarSettingsResponseSchema.parse(response)
    const calendarSettingsById = Object.fromEntries(
      Object.entries(parsedResponse.calendarSettingsById).map(([id, calendarSettings]) => [
        id,
        makeCalendarSettingsViewModel(calendarSettings),
      ])
    )

    return { calendarSettingsById }
  } catch (error) {
    throw new Error(`Failed to parse calendar settings response: ${error}`)
  }
}

export type CalendarSettingsViewModelById = {
  calendarSettingsById: Record<string, CalendarSettingsViewModel>
}
